


































































































































































































































import { Component } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import ajax from '@/lib/ajax';
import { MailboxStatusResponse, MailboxConnectResponse } from '@/models/Activities/EmailDraftRequest';
import { AppModule } from '@/store/modules/app';

// PKCE helper functions
function generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(buffer: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < buffer.length; i++) {
        binary += String.fromCharCode(buffer[i]);
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

@Component({
    name: 'MailboxConnection'
})
export default class MailboxConnection extends GiganautVue {
    status: MailboxStatusResponse | null = null;
    loading = false;
    connecting = false;
    disconnecting = false;
    fetchingSignature = false;
    savingSignature = false;
    signatureEnabled = true;
    editSignatureDialog = false;
    editSignatureName = '';
    editSignatureHtml = '';
    editorKey = 0;
    editorReady = false;
    fetchMessage = '';
    pickerDialog = false;
    availableSignatures: Array<{name: string; html: string}> = [];

    async mounted() {
        await this.loadStatus();
    }

    async loadStatus() {
        this.loading = true;
        try {
            const response = await ajax.get('/api/v1/agency/users/profile/mailbox/status');
            this.status = response.data;
            if (this.status) {
                this.signatureEnabled = this.status.isSignatureEnabled;
            }
        } catch (error: any) {
            // Not connected is not an error
            this.status = { connected: false, isSignatureEnabled: true };
        } finally {
            this.loading = false;
        }
    }

    async fetchSignature() {
        this.fetchingSignature = true;
        this.fetchMessage = '';
        try {
            const response = await ajax.post('/api/v1/agency/users/profile/mailbox/signature/fetch');
            if (response.data.availableSignatures && response.data.availableSignatures.length > 0) {
                // Multiple signatures — show picker dialog
                this.availableSignatures = response.data.availableSignatures;
                this.pickerDialog = true;
            } else if (response.data.message) {
                // Provider returned a message (not supported, no signature, etc.)
                this.fetchMessage = response.data.message;
            } else if (this.status) {
                // Single signature fetched successfully
                this.status.signatureName = response.data.signatureName;
                this.status.signatureHtml = response.data.signatureHtml;
                this.fetchMessage = '';
            }
        } catch (error: any) {
            AppModule.handleApiError(error);
        } finally {
            this.fetchingSignature = false;
        }
    }

    async selectSignature(sig: {name: string; html: string}) {
        this.pickerDialog = false;
        this.savingSignature = true;
        try {
            await ajax.put('/api/v1/agency/users/profile/mailbox/signature', {
                signatureName: sig.name,
                signatureHtml: sig.html,
            });
            if (this.status) {
                this.status.signatureName = sig.name;
                this.status.signatureHtml = sig.html;
            }
        } catch (error: any) {
            AppModule.handleApiError(error);
        } finally {
            this.savingSignature = false;
        }
    }

    openEditSignature() {
        this.editorReady = false;
        this.editSignatureName = this.status?.signatureName || '';
        this.editSignatureHtml = this.status?.signatureHtml || '';
        this.editSignatureDialog = true;
        this.editorKey++;
        this.$nextTick(() => {
            this.editorReady = true;
        });
    }

    closeEditSignature() {
        this.editorReady = false;
        this.editSignatureDialog = false;
    }

    async saveSignature() {
        this.savingSignature = true;
        try {
            await ajax.put('/api/v1/agency/users/profile/mailbox/signature', {
                signatureName: this.editSignatureName,
                signatureHtml: this.editSignatureHtml,
            });
            if (this.status) {
                this.status.signatureName = this.editSignatureName;
                this.status.signatureHtml = this.editSignatureHtml;
            }
            this.editSignatureDialog = false;
        } catch (error: any) {
            AppModule.handleApiError(error);
        } finally {
            this.savingSignature = false;
        }
    }

    async clearSignature() {
        try {
            await ajax.put('/api/v1/agency/users/profile/mailbox/signature', {
                signatureName: '',
                signatureHtml: '',
            });
            if (this.status) {
                this.status.signatureName = '';
                this.status.signatureHtml = '';
            }
        } catch (error: any) {
            AppModule.handleApiError(error);
        }
    }

    async toggleSignatureEnabled() {
        try {
            await ajax.put('/api/v1/agency/users/profile/mailbox/signature/enabled', {
                isSignatureEnabled: this.signatureEnabled,
            });
            if (this.status) {
                this.status.isSignatureEnabled = this.signatureEnabled;
            }
        } catch (error: any) {
            AppModule.handleApiError(error);
            // Revert on failure
            this.signatureEnabled = !this.signatureEnabled;
        }
    }

    async connect() {
        this.connecting = true;
        try {
            // Get authorization URL from backend
            const response = await ajax.post('/api/v1/agency/users/profile/mailbox/connect');
            const data: MailboxConnectResponse = response.data;

            // Generate PKCE code verifier and challenge
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await generateCodeChallenge(codeVerifier);

            // Store code verifier for callback
            sessionStorage.setItem('mailbox_code_verifier', codeVerifier);

            // Build the full authorization URL with PKCE and redirect
            const redirectUri = window.location.origin + '/agency/userprofile/mailbox-callback';
            const authUrl = new URL(data.authorizationUrl);
            authUrl.searchParams.set('redirect_uri', redirectUri);
            authUrl.searchParams.set('code_challenge', codeChallenge);
            authUrl.searchParams.set('code_challenge_method', 'S256');
            authUrl.searchParams.set('state', codeVerifier.substring(0, 16)); // Use part of verifier as state

            // Redirect to Microsoft login
            window.location.href = authUrl.toString();
        } catch (error: any) {
            AppModule.handleApiError(error);
            this.connecting = false;
        }
    }

    async disconnect() {
        if (!confirm('Are you sure you want to disconnect your mailbox?')) {
            return;
        }

        this.disconnecting = true;
        try {
            await ajax.delete('/api/v1/agency/users/profile/mailbox');
            this.status = { connected: false };
        } catch (error: any) {
            AppModule.handleApiError(error);
        } finally {
            this.disconnecting = false;
        }
    }

    formatDate(date: Date | string): string {
        return new Date(date).toLocaleDateString();
    }
}
